import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { eur, initials } from "@/lib/format";
import { PageHeader, StatCard } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ChevronLeft, Gem, CreditCard, CheckCircle2, Circle, ExternalLink, Users, Sparkles,
  Mail, Send, X, Package, ListChecks, MessageSquare, CalendarCheck,
  Wallet, PenLine, Eye, Lock, TrendingUp, Clock, Hourglass, HeartPulse,
} from "lucide-react";
import PerksEditor, { perksToValue, valueToPerks } from "@/components/circle/PerksEditor";

const TILE_NAV = [
  { id: "deliverables", labelKey: "ic.tile_deliverables", icon: Package },
  { id: "todos", labelKey: "ic.tile_todos", icon: ListChecks },
  { id: "clients", labelKey: "ic.tile_clients", icon: Users },
  { id: "chats", labelKey: "ic.tile_chats", icon: MessageSquare },
  { id: "weva", labelKey: "ic.tile_weva", icon: Sparkles },
  { id: "members", labelKey: "ic.tile_members", icon: HeartPulse },
];

export default function InnerCircle() {
  const { user } = useAuth();
  const t = useT();
  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="inner-circle-page">
      <Seo title={t("ic.seo")} noindex />
      <Link to="/app/coach/circles" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="inner-back">
        <ChevronLeft className="h-4 w-4" /> {t("ic.back")}
      </Link>
      <PageHeader
        eyebrow="Inner Circle"
        title="Inner Circle"
        subtitle={t("ic.subtitle")}
      />
      <Tabs defaultValue="einstellungen" className="w-full">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="einstellungen" data-testid="ic-tab-einstellungen">{t("ic.tab_settings")}</TabsTrigger>
          <TabsTrigger value="uebersicht" data-testid="ic-tab-uebersicht">{t("ic.tab_overview")}</TabsTrigger>
          <TabsTrigger value="klienten" data-testid="ic-tab-klienten">{t("ic.tab_clients")}</TabsTrigger>
          <TabsTrigger value="weva" data-testid="ic-tab-weva">{t("ic.tab_weva")}</TabsTrigger>
          <TabsTrigger value="einladungen" data-testid="ic-tab-einladungen">{t("ic.tab_invites")}</TabsTrigger>
        </TabsList>

        <TabsContent value="einstellungen"><SetupTab coachId={user?.id} /></TabsContent>
        <TabsContent value="uebersicht"><OverviewTab /></TabsContent>
        <TabsContent value="klienten"><ClientsTab /></TabsContent>
        <TabsContent value="weva"><WevaTab /></TabsContent>
        <TabsContent value="einladungen"><InvitesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────── Tab: Einstellungen (Setup)
function SetupTab({ coachId }) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [priceEur, setPriceEur] = useState("4.99");
  const [priceCredits, setPriceCredits] = useState("");
  const [description, setDescription] = useState("");
  const [perks, setPerks] = useState({ selected: {}, custom: [] });
  const [published, setPublished] = useState(true);
  const [visibility, setVisibility] = useState("invite_only");
  const [dmHardLimit, setDmHardLimit] = useState(false);
  const [status, setStatus] = useState({ platform_cut: 0.3, coach_share: 0.7 });
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);

  const load = useCallback(async () => {
    if (!coachId) return;
    setLoading(true);
    try {
      const { data: info } = await api.get(`/coach/${coachId}/inner-circle`);
      if (info?.price_cents) setPriceEur((info.price_cents / 100).toFixed(2));
      if (info?.price_credits) setPriceCredits(String(info.price_credits));
      if (info?.description) setDescription(info.description);
      if (info?.published != null) setPublished(!!info.published);
      setPerks(perksToValue(info?.perks));
      setStatus({ platform_cut: info?.platform_cut ?? 0.3, coach_share: info?.coach_share ?? 0.7 });
      setDmHardLimit(!!info?.dm_hard_limit_enabled);
      setVisibility(info?.visibility === "visible" ? "visible" : "invite_only");
    } catch { /* keep defaults */ }
    try {
      const { data: c } = await api.get("/coach/connect/status");
      setConnect({
        has_account: !!c?.account_id || !!c?.has_account,
        charges_enabled: !!c?.charges_enabled,
        payouts_enabled: !!c?.payouts_enabled,
        details_submitted: !!c?.details_submitted,
      });
    } catch { setConnect(null); }
    try {
      const { data: w } = await api.get("/coach/ic-waitlist");
      setWaitlistCount(Number(w?.count || 0));
    } catch { /* silent */ }
    setLoading(false);
  }, [coachId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const cents = Math.round(parseFloat(String(priceEur).replace(",", ".")) * 100) || 0;
    const credits = parseInt(priceCredits) || 0;
    if (cents === 0 && credits === 0) { toast.error(t("ic.err_price")); return; }
    if (cents > 0 && cents < 499) { toast.error(t("ic.err_min_eur")); return; }
    if (credits > 0 && credits < 50) { toast.error(t("ic.err_min_credits")); return; }
    setSaving(true);
    try {
      await api.post("/coach/inner-circle/price", {
        price_cents: cents, price_credits: credits, perks: valueToPerks(perks), published, description, visibility,
      });
      toast.success(published ? t("ic.saved_published") : t("ic.saved_hidden"));
    } catch (err) { toast.error(apiError(err, t("ic.save_err"))); }
    finally { setSaving(false); }
  };

  const toggleDmLimit = async (v) => {
    setDmHardLimit(v);
    try { await api.put("/coach/circles/dm-hard-limit", { enabled: v }); } catch { /* silent */ }
  };

  const startStripe = async () => {
    setConnectBusy(true);
    try {
      const { data } = await api.post("/coach/connect/onboard");
      const url = data?.url || data?.onboarding_url || data?.account_link_url;
      if (url) window.open(url, "_blank", "noopener");
      else toast.error(t("ic.stripe_onboard_err"));
    } catch (err) { toast.error(apiError(err, t("ic.stripe_onboard_err"))); }
    finally { setConnectBusy(false); }
  };

  const priceCents = Math.round(parseFloat(String(priceEur).replace(",", ".")) * 100) || 0;
  const hasConnect = !!connect?.charges_enabled && !!connect?.payouts_enabled;
  const hasPrice = priceCents >= 499;
  const cardReady = hasConnect && hasPrice;

  if (loading) return <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{t("ic.setup_intro")}</p>

      {/* Payout readiness */}
      {!cardReady ? (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="ic-payout-checklist">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" />
            <p className="font-semibold text-foreground">{t("ic.enable_card")}</p>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t("ic.enable_card_desc")}</p>
          <div className="mt-4 flex items-start gap-3">
            {hasConnect ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{t("ic.step1")}</p>
              <p className="text-xs text-muted-foreground">
                {!connect?.has_account ? t("ic.stripe_not_started")
                  : connect.details_submitted && !hasConnect ? t("ic.stripe_submitted")
                  : hasConnect ? t("ic.stripe_connected") : t("ic.stripe_incomplete")}
              </p>
              {!hasConnect && (
                <Button variant="outline" size="sm" className="mt-2 gap-1.5 rounded-full" onClick={startStripe} disabled={connectBusy} data-testid="ic-stripe-onboard-btn">
                  {connectBusy ? t("ic.opening_stripe") : (connect?.has_account ? t("ic.continue_onboarding") : t("ic.stripe_start"))}
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-start gap-3">
            {hasPrice ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{t("ic.step2")}</p>
              <p className="text-xs text-muted-foreground">{hasPrice ? t("ic.price_set", { price: eur(priceCents) }) : t("ic.price_enter")}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50/50 p-5" data-testid="ic-payout-ready">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <p className="font-semibold text-foreground">{t("ic.card_active")}</p>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{t("ic.card_active_desc", { price: eur(priceCents), share: Math.round(status.coach_share * 100) })}</p>
        </div>
      )}

      {/* Price EUR */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.price_eur")}</label>
        <div className="mt-1.5 flex items-center gap-2">
          <span className="text-lg text-muted-foreground">€</span>
          <Input data-testid="ic-price" value={priceEur} inputMode="decimal" onChange={(e) => setPriceEur(e.target.value)} className="max-w-[160px]" />
          <span className="text-sm text-muted-foreground">{t("ic.month_suffix")}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("ic.only_credits_hint")}</p>
      </div>

      {/* Price credits */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.price_credits")}</label>
        <div className="mt-1.5 flex items-center gap-2">
          <Gem className="h-4 w-4 text-primary" />
          <Input data-testid="ic-price-credits" value={priceCredits} inputMode="numeric" placeholder={t("ic.price_credits_ph")} onChange={(e) => setPriceCredits(e.target.value)} className="max-w-[160px]" />
          <span className="text-sm text-muted-foreground">{t("ic.credits_month")}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t("ic.credits_auto_hint")}</p>
        <p className="mt-1 text-xs italic text-muted-foreground">
          {t("ic.revenue_split", { platform: Math.round(status.platform_cut * 100), coach: Math.round(status.coach_share * 100) })}
          {status.platform_cut === 0.3 ? t("ic.coach_pro_hint") : ""}
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.description_label")}</label>
        <Textarea data-testid="ic-description" className="mt-1.5 min-h-[80px]" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("ic.description_ph")} />
      </div>

      {/* Perks */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.whats_included")}</label>
        <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{t("ic.core_hint")}</p>
        <PerksEditor value={perks} onChange={setPerks} />
      </div>

      {/* Visibility */}
      <div data-testid="ic-visibility-section">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.visibility")}</label>
        <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{t("ic.visibility_hint")}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" onClick={() => setVisibility("visible")} data-testid="ic-visibility-visible" className={`flex items-start gap-3 rounded-xl border p-3 text-left ${visibility === "visible" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
            <Eye className={`mt-0.5 h-5 w-5 ${visibility === "visible" ? "text-primary" : "text-muted-foreground"}`} />
            <div><p className="text-sm font-medium text-foreground">{t("ic.vis_public")}</p><p className="text-xs text-muted-foreground">{t("ic.vis_public_hint")}</p></div>
          </button>
          <button type="button" onClick={() => setVisibility("invite_only")} data-testid="ic-visibility-invite-only" className={`flex items-start gap-3 rounded-xl border p-3 text-left ${visibility === "invite_only" ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
            <Lock className={`mt-0.5 h-5 w-5 ${visibility === "invite_only" ? "text-primary" : "text-muted-foreground"}`} />
            <div><p className="text-sm font-medium text-foreground">{t("ic.vis_invite")}</p><p className="text-xs text-muted-foreground">{t("ic.vis_invite_hint")}</p></div>
          </button>
        </div>
      </div>

      {/* Published */}
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{t("ic.published")}</p>
          <p className="text-xs text-muted-foreground">{t("ic.published_hint")}</p>
        </div>
        <Switch checked={published} onCheckedChange={setPublished} data-testid="ic-published" />
      </div>

      {!published && waitlistCount > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/10 p-3" data-testid="ic-waitlist-banner">
          <Users className="h-4 w-4 text-primary" />
          <div>
            <p className="text-sm font-semibold text-primary">{t("ic.waitlist", { n: waitlistCount })}</p>
            <p className="text-xs text-muted-foreground">{t("ic.waitlist_hint")}</p>
          </div>
        </div>
      )}

      {/* DM hard limit */}
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{t("ic.dm_limit")}</p>
          <p className="text-xs text-muted-foreground">{t("ic.dm_limit_hint")}</p>
        </div>
        <Switch checked={dmHardLimit} onCheckedChange={toggleDmLimit} data-testid="ic-dm-hard-limit" />
      </div>

      <Button onClick={save} disabled={saving} className="w-full rounded-full sm:w-auto" data-testid="ic-save">
        {saving ? t("ic.saving") : t("ic.save")}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────── Tab: Übersicht
function OverviewTab() {
  const t = useT();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/coach/circles/overview?circle_type=inner")
      .then(({ data }) => { if (!cancelled) setOverview(data); })
      .catch((err) => { if (!cancelled) toast.error(apiError(err, t("ic.overview_err"))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  const o = overview || {};
  const tiles = [
    { label: t("ic.ov_active_members"), value: o.active_members ?? 0, icon: Users, testId: "tile-active-members" },
    { label: t("ic.ov_snapshots"), value: o.snapshot_requests_confirmed ?? 0, icon: CheckCircle2, testId: "tile-snapshots" },
    { label: t("ic.ov_entries"), value: o.user_entries_received ?? 0, icon: PenLine, testId: "tile-entries" },
    { label: t("ic.ov_bookings"), value: o.bookings_received ?? 0, icon: CalendarCheck, testId: "tile-bookings" },
    { label: t("ic.ov_offerings"), value: (o.active_offerings || []).length, icon: Package, testId: "tile-offerings" },
  ];
  const priceCents = user?.inner_circle_price_cents || 0;
  const members = o.active_members ?? 0;
  const recurringCents = members * priceCents;
  return (
    <div>
      <div className="mb-5 rounded-2xl border border-primary/25 bg-primary/5 p-5" data-testid="ic-income-banner">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary"><Wallet className="h-3.5 w-3.5" /> {t("ic.ov_earnings")}</p>
            <p className="mt-1 font-display text-3xl text-foreground" data-testid="ic-income-month">{eur(o.month_earnings_cents ?? 0)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("rw3.ic_this_month")}</p>
          </div>
          <div className="flex gap-6">
            <div className="text-right">
              <p className="font-display text-xl text-foreground">{members}</p>
              <p className="text-xs text-muted-foreground">{t("ic.ov_active_members")}</p>
            </div>
            {priceCents > 0 ? (
              <div className="text-right" data-testid="ic-income-recurring">
                <p className="font-display text-xl text-foreground">{eur(recurringCents)}</p>
                <p className="text-xs text-muted-foreground">{t("rw3.ic_recurring_est")}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{tiles.map((tile) => <StatCard key={tile.testId} {...tile} />)}</div>
      <div className="mt-6 rounded-2xl border border-border bg-card p-5">
        <p className="mb-3 text-sm font-semibold text-foreground">{t("ic.deliverables_title")}</p>
        {(o.deliverables || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ic.no_deliverables")}</p>
        ) : (
          <ul className="space-y-1.5" data-testid="deliverables-preview">
            {o.deliverables.map((d, i) => (
              <li key={i} className="flex items-center justify-between rounded-xl bg-secondary/60 px-3 py-2 text-sm">
                <span className="text-foreground">{d.kind}</span>
                <span className="text-muted-foreground">{t("ic.per_month", { n: d.count_per_month })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-8">
        <p className="mb-3 font-display text-lg text-foreground">{t("ic.areas")}</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TILE_NAV.map((tile) => (
            <Link key={tile.id} to={`/app/coach/circles/inner/tile/${tile.id}`} data-testid={`circle-tile-${tile.id}`} className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground"><tile.icon className="h-5 w-5" /></div>
              <span className="font-medium text-foreground">{t(tile.labelKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────── Tab: Klient:innen (IC members)
function ClientsTab() {
  const t = useT();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/coach/my-clients")
      .then(({ data }) => { if (!cancelled) setClients((data?.clients || []).filter((c) => c.is_inner_circle)); })
      .catch(() => { if (!cancelled) setClients([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;
  if (clients.length === 0) return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Users className="h-6 w-6" /></div>
      <p className="mt-4 font-display text-lg text-foreground">{t("ic.no_members")}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t("ic.no_members_hint")}</p>
    </div>
  );
  return (
    <div className="space-y-2">
      {clients.map((c) => (
        <div key={c.client_id} data-testid={`ic-client-${c.client_id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
          <Avatar className="h-10 w-10">
            {c.avatar_url && <AvatarImage src={c.avatar_url} alt={c.name} />}
            <AvatarFallback>{initials(c.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="truncate font-semibold text-foreground">{c.name}</p>
              <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">IC</span>
            </div>
            {c.email && <p className="truncate text-xs text-muted-foreground">{c.email}</p>}
          </div>
          {(c.sessions_upcoming || c.sessions_past) ? (
            <p className="shrink-0 text-xs text-muted-foreground">{c.sessions_upcoming ? t("ic.planned", { n: c.sessions_upcoming }) : ""}{c.sessions_upcoming && c.sessions_past ? " · " : ""}{c.sessions_past ? t("ic.past", { n: c.sessions_past }) : ""}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────── Tab: WEVA (group report)
function WevaTab() {
  const t = useT();
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/coach/inner-circle-report?days=${days}`)
      .then(({ data }) => { if (!cancelled) setReport(data); })
      .catch((err) => { if (!cancelled) toast.error(apiError(err, t("ic.weva_err"))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);
  return (
    <div>
      <div className="mb-4 flex gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)} data-testid={`ic-report-period-${d}`} className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${days === d ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {d === 7 ? t("ic.snapshot7") : d === 30 ? t("ic.days30") : t("ic.history90")}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-2xl" />)}</div>
      ) : !report ? null : (
        <div className="space-y-5">
          <div>
            <p className="font-display text-xl text-foreground">{report.members === 1 ? t("ic.members_one", { n: report.members }) : t("ic.members_other", { n: report.members })}</p>
            <p className="text-xs text-muted-foreground">{t("ic.period", { n: report.period_days })}</p>
          </div>
          {report.beliefs?.length ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.top_beliefs")}</p>
              <div className="space-y-1.5">
                {report.beliefs.slice(0, 8).map((b, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">{b.count}×</span>
                    <span className="flex-1 text-sm text-foreground">„{b.text}{'"'}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {report.upper_limits?.length ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.upper_limits")}</p>
              <div className="space-y-1.5">
                {report.upper_limits.slice(0, 8).map((u, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">{u.count}×</span>
                    <span className="flex-1 text-sm text-foreground">{u.area}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {(!report.beliefs?.length && !report.upper_limits?.length) ? (
            <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">{t("ic.not_enough")}</p>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────── Tab: Einladungen
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
function parseEmails(raw) {
  return Array.from(new Set(raw.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e && EMAIL_RE.test(e))));
}
function fmtAcceptTime(hours, t) {
  if (!hours) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} ${t("ic.min")}`;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} ${t("ic.days")}`;
}

function InvitesTab() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/coach/inner-circle/invitations").then(({ data }) => data).catch(() => ({ invitations: [] })),
        api.get("/coach/inner-circle/invite-analytics").then(({ data }) => data).catch(() => null),
      ]);
      setItems(r1?.invitations || []);
      setAnalytics(r2);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = { pending: [], accepted: [], revoked: [] };
  for (const it of items) {
    if (it.status === "accepted") grouped.accepted.push(it);
    else if (it.status === "revoked") grouped.revoked.push(it);
    else grouped.pending.push(it);
  }

  const submit = async () => {
    const emails = parseEmails(emailInput);
    if (!emails.length) { toast.error(t("ic.err_email")); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/coach/inner-circle/invite", { emails, note: noteInput.trim() || undefined });
      setEmailInput(""); setNoteInput("");
      toast.success(t("ic.invites_created", { n: r?.invitations_created ?? emails.length }));
      await load();
    } catch (err) { toast.error(apiError(err, t("ic.invites_err"))); }
    finally { setBusy(false); }
  };

  const revoke = async (inv) => {
    try { await api.delete(`/coach/inner-circle/invitations/${inv.id}`); toast.success(t("ic.invite_revoked")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6">
      {/* Composer */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.emails_label")}</label>
        <Textarea data-testid="invite-email-input" className="mt-1.5 min-h-[66px]" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="anna@example.com, ben@example.com …" />
        <p className="mt-1 text-xs text-muted-foreground">{t("ic.emails_hint")}</p>
        <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("ic.note_label")}</label>
        <Input data-testid="invite-note-input" className="mt-1.5" value={noteInput} maxLength={200} onChange={(e) => setNoteInput(e.target.value)} placeholder={t("ic.note_ph")} />
        <div className="mt-3 flex justify-end">
          <Button onClick={submit} disabled={busy || !emailInput.trim()} className="gap-1.5 rounded-full" data-testid="invite-submit">
            <Send className="h-4 w-4" /> {busy ? t("ic.sending") : t("ic.invite")}
          </Button>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="invite-analytics">
          <InviteKpi label={t("ic.kpi_sent")} value={String(analytics.sent ?? 0)} icon={Mail} />
          <InviteKpi label={t("ic.kpi_accept_rate")} value={`${analytics.accept_rate ?? 0}%`} icon={TrendingUp} accent />
          <InviteKpi label={t("ic.kpi_accepted")} value={String(analytics.accepted ?? 0)} icon={CheckCircle2} />
          <InviteKpi label={t("ic.kpi_pending")} value={String(analytics.pending ?? 0)} icon={Hourglass} />
          <InviteKpi label={t("ic.kpi_revoked")} value={String(analytics.revoked ?? 0)} icon={X} />
          <InviteKpi label={t("ic.kpi_avg_time")} value={fmtAcceptTime(analytics.avg_accept_hours ?? 0, t)} icon={Clock} />
          <InviteKpi label={t("ic.kpi_active")} value={String(analytics.active_subscribers ?? 0)} icon={Users} />
          <InviteKpi label={t("ic.kpi_retention")} value={String(analytics.retention_30d ?? 0)} icon={CheckCircle2} />
        </div>
      )}

      {/* Lists */}
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 rounded-2xl" />)}</div>
      ) : (
        <>
          <InviteSection title={t("ic.sec_pending")} empty={t("ic.sec_pending_empty")} items={grouped.pending} onRevoke={revoke} />
          <InviteSection title={t("ic.sec_accepted")} empty={t("ic.sec_accepted_empty")} items={grouped.accepted} />
          {grouped.revoked.length > 0 && <InviteSection title={t("ic.sec_revoked")} empty="—" items={grouped.revoked} />}
        </>
      )}
    </div>
  );
}

function InviteKpi({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
      <Icon className={`h-4 w-4 ${accent ? "text-primary-foreground" : "text-primary"}`} />
      <p className={`mt-2 font-display text-2xl font-semibold ${accent ? "text-primary-foreground" : "text-foreground"}`}>{value}</p>
      <p className={`text-xs ${accent ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{label}</p>
    </div>
  );
}

function InviteSection({ title, empty, items, onRevoke }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">{empty}</p>
      ) : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} data-testid={`invite-row-${i.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground"><Mail className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">{i.client_name || i.email || i.client_id}</p>
                {i.client_name && i.email && <p className="truncate text-xs text-muted-foreground">{i.email}</p>}
                {i.note && <p className="truncate text-xs italic text-muted-foreground">„{i.note}{'"'}</p>}
              </div>
              {onRevoke && (
                <button type="button" onClick={() => onRevoke(i)} data-testid={`invite-revoke-${i.id}`} className="text-rose-500 hover:text-rose-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
